import { DateTime } from "luxon";
import { logger } from "@portal/app/_common/lib/logger";
import { parseGradYear } from "@portal/app/_common/lib/gradYear";
import { DEFAULT_HS_GRAD_YEAR_MIN } from "@portal/app/_common/types/program";
import type { QuestionOption } from "../../FormFieldRenderer";

interface GradYearBoundsResult {
  options: QuestionOption[] | undefined;
  /** Effective inclusive range for client-side validation; null = no client-side constraint */
  bounds: { min: number; max: number } | null;
}

/**
 * Resolves the matched program's hsGradYearMin/hsGradYearMax into the effective
 * client-side constraint for the GRAD_YEAR step: the select options are filtered
 * to the range and the same range drives the step's Yup validation, so the two
 * cannot disagree. A null bound falls back to the Program contract's documented
 * defaults (DEFAULT_HS_GRAD_YEAR_MIN / the current year — a graduation year is a
 * past event). Non-canonical option values can never pass the year validation, so
 * they are dropped along with out-of-range years.
 *
 * When the bounds exclude every configured option (misconfigured program), the
 * original options are returned with bounds disabled so the user can still
 * complete the step — the server-side eligibility gate then rejects the
 * submission at the confirm step with the specific graduation-year warning.
 */
export function applyGradYearBounds(
  options: QuestionOption[] | undefined,
  hsGradYearMin?: number | null,
  hsGradYearMax?: number | null
): GradYearBoundsResult {
  if (hsGradYearMin == null && hsGradYearMax == null) {
    return { options, bounds: null };
  }

  const bounds = {
    min: hsGradYearMin ?? DEFAULT_HS_GRAD_YEAR_MIN,
    max: hsGradYearMax ?? DateTime.now().year,
  };

  if (bounds.min > bounds.max) {
    logger.warn(
      `[applyGradYearBounds] Program grad-year minimum ${bounds.min} exceeds maximum ${bounds.max}; disabling client bounds and deferring to the server-side eligibility check`
    );
    return { options, bounds: null };
  }

  if (!options || options.length === 0) {
    return { options, bounds };
  }

  const filtered = options.filter((option) => {
    const year = parseGradYear(String(option.value));
    return year != null && year >= bounds.min && year <= bounds.max;
  });

  if (filtered.length === 0) {
    logger.warn(
      `[applyGradYearBounds] Program grad-year bounds ${bounds.min}–${bounds.max} exclude every configured year option; leaving options unfiltered and deferring to the server-side eligibility check`
    );
    return { options, bounds: null };
  }

  return { options: filtered, bounds };
}
